<template>
  <div v-if="isOpen" class="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 text-left">
    <div class="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden border border-slate-200">
      <!-- Header -->
      <div class="px-6 py-4 bg-[#0d4169] text-white flex items-center justify-between rounded-t-xl shrink-0">
        <div class="flex flex-col">
          <h2 class="text-base font-bold m-0">Manage sub-departments</h2>
          <p class="text-[10px] text-white/80 font-bold tracking-wider mt-0.5">Department: <span class="text-emerald-300 font-medium">{{ department.name }}</span></p>
        </div>
        <button @click="$emit('close')" class="p-1 hover:bg-white/10 rounded-full transition-colors text-white">
          <svg xmlns="http://www.w3.org/2000/svg" class="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <!-- Registration Form -->
      <div class="px-6 py-4 bg-white border-b border-slate-100">
        <form @submit.prevent="handleSubmit" class="flex items-end gap-3">
          <div class="flex-[1.5]">
            <label class="block text-[10px] font-bold text-slate-500 mb-1">Sub-Department Name</label>
            <input
              v-model="form.name"
              type="text"
              placeholder="e.g. Sales Team A"
              class="w-full px-3 py-[7px] text-xs border border-slate-200 rounded focus:outline-none focus:border-[#0d4169] transition-all font-medium"
              required
            />
          </div>
          <div class="flex-1">
            <label class="block text-[10px] font-bold text-slate-500 mb-1">Remark (Optional)</label>
            <input
              v-model="form.remark"
              type="text"
              placeholder="Add notes..."
              class="w-full px-3 py-[7px] text-xs border border-slate-200 rounded focus:outline-none focus:border-[#0d4169] transition-all font-medium"
            />
          </div>

          <!-- Image Upload Box -->
          <div class="w-24">
            <label class="block text-[10px] font-bold text-slate-500 mb-1">Image (Optional)</label>
            <label 
              for="sub-dept-image-upload" 
              class="flex items-center justify-center border border-dashed border-slate-200 rounded p-1 cursor-pointer hover:border-[#0d4169] hover:bg-slate-50 transition-all group h-[32px] relative overflow-hidden"
            >
              <div v-if="form.image" class="w-full h-full flex items-center justify-center relative">
                <img :src="resolveCompanyLogoUrl(form.image)" class="max-w-full max-h-6 object-contain">
                <button @click.prevent="form.image = ''" class="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-3.5 h-3.5 flex items-center justify-center text-[8px] shadow-sm hover:bg-red-600 z-10">✕</button>
              </div>
              <div v-else class="flex items-center gap-1.5 text-slate-300 group-hover:text-[#0d4169]">
                 <svg v-if="!uploadingImage" class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg>
                 <svg v-else class="w-4 h-4 animate-spin text-[#0d4169]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                 <span class="text-[9px] font-bold text-slate-400 group-hover:text-[#0d4169]">{{ uploadingImage ? '...' : 'Upload' }}</span>
              </div>
              <input id="sub-dept-image-upload" type="file" class="hidden" accept="image/*" @change="handleImageUpload">
            </label>
          </div>

          <!-- Buttons -->
          <div class="flex items-center gap-2 h-[32px]">
            <button
              v-if="editingItem"
              type="button"
              @click="cancelEdit"
              class="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-600 text-[10px] font-bold rounded transition-all h-full"
            >
              Cancel
            </button>
            <button
              type="submit"
              :disabled="saving || uploadingImage"
              class="min-w-[120px] px-4 py-1.5 bg-[#0d4169] hover:bg-[#0a3354] text-white text-[10px] font-bold rounded transition-all shadow-sm flex justify-center items-center gap-2 h-full disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
            >
              <div
                v-if="saving"
                class="h-4 w-4 border-2 border-white/30 border-t-transparent rounded-full animate-spin"
              ></div>
              <span v-if="!saving">{{ editingItem ? 'Update' : 'Save' }} Sub-Department</span>
            </button>
          </div>
        </form>
      </div>

      <!-- Content Area (Table) -->
      <div class="flex-1 overflow-y-auto p-6 bg-white pt-2">
        <tr v-if="loading">
  <td colspan="9" class="text-center py-10">
    <div class="flex items-center justify-center">
      <div class="h-8 w-8 border-4 border-gray-300 border-t-transparent rounded-full animate-spin"></div>
    </div>
  </td>
</tr>

        <div v-else-if="subDepartments.length === 0" class="flex flex-col items-center justify-center p-12 text-slate-400 gap-2 border border-dashed border-slate-200 rounded-lg">
          <p class="text-xs font-medium text-slate-400">No Sub-Departments found.</p>
        </div>

        <div v-else class="overflow-hidden">
          <table class="w-full border-collapse">
            <thead>
              <tr class="bg-slate-50 border-b border-slate-200">
                <th class="px-3 py-2 text-left text-[10px] font-bold text-slate-500">No.</th>
                <th class="px-3 py-2 text-left text-[10px] font-bold text-slate-500">Image</th>
                <th class="px-3 py-2 text-left text-[10px] font-bold text-slate-500">Name</th>
                <th class="px-3 py-2 text-left text-[10px] font-bold text-slate-500">Remark</th>
                <th class="px-3 py-2 text-left text-[10px] font-bold text-slate-500">Created At</th>
                <th class="px-3 py-2 text-right text-[10px] font-bold text-slate-500">Action</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-slate-100">
              <tr v-for="(sub, index) in subDepartments" :key="sub.id" class="hover:bg-slate-100/50 transition-colors border-b">
                <td class="px-3 py-2 text-[11px] text-slate-400 font-medium">{{ index + 1 }}</td>
                <td class="px-3 py-2">
                  <div class="inline-flex">
                    <img v-if="sub.image" :src="resolveCompanyLogoUrl(sub.image)" class="w-7 h-6 object-contain">
                    <div v-else class="w-7 h-6 rounded bg-slate-100 flex items-center justify-center text-[8px] text-slate-400 font-bold uppercase shrink-0">
                      {{ sub.name.charAt(0) }}
                    </div>
                  </div>
                </td>
                <td class="px-3 py-2 text-[11px] font-bold text-slate-700">{{ sub.name }}</td>
                <td class="px-3 py-2 text-[11px] text-slate-500 italic">{{ sub.remark || '-' }}</td>
                <td class="px-3 py-2 text-[11px] text-slate-400 tracking-tighter">{{ formatDate(sub.created_at) }}</td>
                <td class="px-3 py-2 text-right">
                  <div class="inline-flex items-center justify-end gap-2">
                    <label class="toggle-switch" title="Toggle status">
                      <input
                        type="checkbox"
                        :checked="sub.status === 1"
                        @change="toggleSubDepartmentStatus(sub)"
                      >
                      <span class="slider"></span>
                    </label>
                    <button
                      @click="editItem(sub)"
                      class="p-1 px-2 text-blue-500 hover:text-blue-600 transition-all font-bold text-[10px] border border-blue-100 rounded hover:bg-blue-50"
                    >
                      Edit
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed, watch } from 'vue'
import { apiFetch, WS_BASE_URL, normalizeUploadValue, resolveCompanyLogoUrl } from '../../utils/api'
import store from '../../store'

/* globals defineProps, defineEmits */
const props = defineProps({
  isOpen: Boolean,
  department: Object
})

defineEmits(['close'])

const subDepartments = ref([])
const loading = ref(false)
const saving = ref(false)
const uploadingImage = ref(false)
const editingItem = ref(null)

const form = ref({
  name: '',
  remark: '',
  image: ''
})

let subDeptSyncSocket = null
let subDeptSyncReconnectTimer = null

const currentUser = computed(() => store.getters.currentUser)
const companyId = computed(() => currentUser.value?.companyId || currentUser.value?.company_id || null)
const branchId = computed(() => currentUser.value?.branchId || currentUser.value?.branch_id || null)
const userId = computed(() => currentUser.value?.id || null)

function formatDate(val) {
  if (!val) return '-'
  return new Date(val).toISOString().split('T')[0]
}

async function handleImageUpload(event) {
  const file = event.target.files[0]
  if (!file) return

  // 3MB limit
  if (file.size > 3 * 1024 * 1024) {
    alert('File is too large. Max 3MB.')
    event.target.value = ''
    return
  }

  uploadingImage.value = true
  try {
    const formData = new FormData()
    formData.append('logo', file)

    const res = await apiFetch('/upload', {
      method: 'POST',
      body: formData
    })

    if (res.ok) {
      const data = await res.json()
      form.value.image = normalizeUploadValue(data.url)
    } else {
      throw new Error('Upload failed')
    }
  } catch (err) {
    console.error('Error uploading image:', err)
    alert('Failed to upload image.')
  } finally {
    uploadingImage.value = false
    event.target.value = ''
  }
}

async function loadSubDepartments() {
  if (!props.department?.id) return
  loading.value = true
  try {
    const response = await apiFetch(`/sub-departments/department/${props.department.id}`, { method: 'GET' })
    if (response.ok) {
      subDepartments.value = await response.json()
      subDepartments.value.sort((a, b) => a.name.localeCompare(b.name))
    }
  } catch (err) {
    console.error('Error loading sub-departments:', err)
  } finally {
    loading.value = false
  }
}

async function handleSubmit() {
  if (!form.value.name) return
  saving.value = true
  try {
    const isEdit = !!editingItem.value
    const endpoint = isEdit ? `/sub-departments/${editingItem.value.id}` : '/sub-departments'
    const method = isEdit ? 'PUT' : 'POST'
    
    const payload = {
      ...form.value,
      image: normalizeUploadValue(form.value.image),
      departmentId: props.department.id,
      companyId: branchId.value ? null : companyId.value,
      branchId: branchId.value || null,
      [isEdit ? 'updated_by' : 'created_by']: userId.value
    }

    const response = await apiFetch(endpoint, {
      method,
      body: JSON.stringify(payload)
    })

    if (response.ok) {
      await loadSubDepartments()
      cancelEdit()
    } else {
      const data = await response.json()
      alert(data.message || 'Error processing request')
    }
  } catch (err) {
    console.error('Error saving sub-department:', err)
  } finally {
    saving.value = false
  }
}

async function toggleSubDepartmentStatus(sub) {
  try {
    const res = await apiFetch(`/sub-departments/${sub.id}/toggle-status`, { method: 'PATCH' })
    if (res.ok) {
      await loadSubDepartments()
    } else {
      const data = await res.json().catch(() => ({}))
      alert(data.message || 'Failed to toggle status')
    }
  } catch (err) {
    console.error('Error toggling sub-department status:', err)
    alert('Failed to toggle status')
  }
}

function editItem(sub) {
  editingItem.value = sub
  form.value.name = sub.name
  form.value.remark = sub.remark || ''
  form.value.image = normalizeUploadValue(sub.image)
}

function cancelEdit() {
  editingItem.value = null
  form.value.name = ''
  form.value.remark = ''
  form.value.image = ''
}

function disconnectSubDeptSyncRealtime() {
  if (subDeptSyncReconnectTimer) {
    clearTimeout(subDeptSyncReconnectTimer)
    subDeptSyncReconnectTimer = null
  }
  if (subDeptSyncSocket) {
    try {
      subDeptSyncSocket.close()
    } catch (_) {
      // ignore close failures
    }
    subDeptSyncSocket = null
  }
}

function connectSubDeptSyncRealtime() {
  disconnectSubDeptSyncRealtime()

  if (!props.isOpen) return

  const user = currentUser.value
  if (!user || typeof WebSocket === 'undefined') return

  const scopedCompanyId = user.companyId || user.company_id || user.company?.id
  if (!scopedCompanyId) return

  const scopedBranchId = user.branchId || user.branch_id || user.branch?.id || null
  const socket = new WebSocket(WS_BASE_URL)
  subDeptSyncSocket = socket

  socket.addEventListener('open', () => {
    socket.send(JSON.stringify({
      type: 'frontend.subscribe',
      company_id: scopedCompanyId,
      branch_id: scopedBranchId,
    }))
  })

  socket.addEventListener('message', (event) => {
    try {
      const message = JSON.parse(event.data)
      if (message.event?.startsWith('sub_department.')) {
        loadSubDepartments()
      }
    } catch (_) {
      // ignore malformed realtime payloads
    }
  })

  socket.addEventListener('close', () => {
    subDeptSyncSocket = null
    if (props.isOpen) {
      subDeptSyncReconnectTimer = setTimeout(() => {
        connectSubDeptSyncRealtime()
      }, 3000)
    }
  })
}

watch(() => props.isOpen, (newVal) => {
  if (newVal) {
    loadSubDepartments()
    cancelEdit()
    connectSubDeptSyncRealtime()
  } else {
    disconnectSubDeptSyncRealtime()
  }
})

onMounted(() => {
  if (props.isOpen) {
    loadSubDepartments()
    connectSubDeptSyncRealtime()
  }
})

onUnmounted(() => {
  disconnectSubDeptSyncRealtime()
})
</script>

<style scoped>
th, td {
  white-space: nowrap;
}

/* Small toggle switch */
.toggle-switch {
  position: relative;
  display: inline-block;
  width: 28px;
  height: 15px;
  cursor: pointer;
}
.toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}
.slider {
  position: absolute;
  inset: 0;
  background: #cbd5e1;
  border-radius: 34px;
  transition: 0.2s;
}
.slider::before {
  content: '';
  position: absolute;
  width: 10px;
  height: 10px;
  left: 2px;
  top: 2.5px;
  background: white;
  border-radius: 50%;
  transition: 0.2s;
}
.toggle-switch input:checked + .slider {
  background: #22c55e;
}
.toggle-switch input:not(:checked) + .slider {
  background: #ef4444;
}
.toggle-switch input:checked + .slider::before {
  transform: translateX(13px);
}
</style>
